import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Drawer,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { CheckboxGroup } from "@/components/ui/checkbox-group";
import { Close } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { ViewDataConfiguration } from "@/entities/views/types";
import Tabs from "@/components/controls/Tabs";
import TabBadge from "@/components/ui/tab-badge";
import DeleteViewModal from "./DeleteViewModal";
import {
  DragDropContext,
  Droppable,
  DropResult,
  DragUpdate
} from "@hello-pangea/dnd";
import { IconButton } from "@/components/ui/icon-button";
import SearchButtonInput from "@/components/ui/search-button-input";
import NewDraggableViewItem from "@/components/layouts/ViewsSelection/NewDraggableViewItem";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedViews: ViewDataConfiguration[];
  onApply: (
    newViews: ViewDataConfiguration[],
    oldViews: ViewDataConfiguration[]
  ) => void;
  onDeleteView: (view: ViewDataConfiguration) => void | Promise<boolean>;
  className?: string;
}

export type ViewFilterType = "all" | "system" | "custom";

const getViewFilterOptions = (
  t: any,
  viewStates: ViewDataConfiguration[]
): { [key: string]: string | JSX.Element } => {
  const systemCount = viewStates.filter((v) => v.isSystem).length;
  const customCount = viewStates.filter((v) => !v.isSystem).length;

  return {
    all: t("datatoolbar.views.slicers.all"),
    system: (
      <TabBadge
        label={t("datatoolbar.views.slicers.system")}
        count={systemCount}
      />
    ),
    custom: (
      <TabBadge
        label={t("datatoolbar.views.slicers.custom")}
        count={customCount}
      />
    )
  };
};

const NewViewsSelection: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedViews,
  onApply,
  onDeleteView,
  className
}) => {
  const { t, i18n } = useTranslation();

  const [viewStates, setViewStates] =
    useState<ViewDataConfiguration[]>(selectedViews);
  const [activeFilter, setActiveFilter] = useState<ViewFilterType>("all");
  const [viewToDelete, setViewToDelete] =
    useState<ViewDataConfiguration | null>(null);
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    destinationIndex: number | null;
  }>({
    isDragging: false,
    destinationIndex: null
  });
  const [searchTerm, setSearchTerm] = useState("");

  const visibleViewsCount = useMemo(
    () => viewStates.filter((v) => v.isOpen).length,
    [viewStates]
  );

  const wasOpenRef = useRef(false);
  // Snapshot the views only on the open transition. Re-snapshotting whenever
  // `selectedViews` changes identity (e.g. a background views refetch) while
  // the drawer is open would silently revert in-progress edits before Apply.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setViewStates(selectedViews);
      setActiveFilter("all");
      setViewToDelete(null);
      setSearchTerm("");
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, selectedViews]);

  const handleApply = async () => {
    onApply(viewStates, selectedViews);
    onClose();
  };

  const handleViewChange = (view: ViewDataConfiguration, checked: boolean) => {
    if (!checked && visibleViewsCount <= 1) return;

    setViewStates((currentViews) =>
      currentViews.map((v) =>
        v.id === view.id ? { ...v, isOpen: checked } : v
      )
    );
  };

  const getIsCheckboxDisabled = useCallback(
    (view: ViewDataConfiguration) => {
      if (viewStates.length <= 1) return true;
      if (visibleViewsCount <= 1 && view.isOpen) return true;
      return false;
    },
    [viewStates.length, visibleViewsCount]
  );

  const handleDeleteView = (view: ViewDataConfiguration) => {
    setViewToDelete(view);
  };

  const handleConfirmDelete = async () => {
    if (!viewToDelete) return;
    const removedView = viewToDelete;
    const removedIndex = viewStates.findIndex((v) => v.id === removedView.id);
    setViewStates((currentViews) =>
      currentViews.filter((v) => v.id !== removedView.id)
    );
    setViewToDelete(null);

    let didDelete: void | boolean;
    try {
      didDelete = await onDeleteView(removedView);
    } catch {
      didDelete = false;
    }
    // Restore the row at its original position if the delete failed, so the
    // drawer doesn't keep showing it as removed while it still exists.
    if (didDelete === false) {
      setViewStates((currentViews) =>
        currentViews.some((v) => v.id === removedView.id)
          ? currentViews
          : [
              ...currentViews.slice(0, removedIndex),
              removedView,
              ...currentViews.slice(removedIndex)
            ]
      );
    }
  };

  const handleRename = (view: ViewDataConfiguration, newName: string) => {
    if (view.isSystem || !newName.trim()) return;

    const trimmedName = newName.trim().slice(0, 100);

    setViewStates((currentViews) =>
      currentViews.map((v) =>
        v.id === view.id ? { ...v, name: trimmedName } : v
      )
    );
  };

  const handleDragUpdate = (update: DragUpdate) => {
    if (!update.destination) {
      setDragState({ isDragging: true, destinationIndex: null });
      return;
    }
    setDragState({
      isDragging: true,
      destinationIndex: update.destination.index
    });
  };

  const handleDragEnd = (result: DropResult) => {
    setDragState({ isDragging: false, destinationIndex: null });
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;

    setViewStates((currentViews) => {
      const newViews = [...currentViews];
      const [removed] = newViews.splice(source.index, 1);
      newViews.splice(destination.index, 0, removed);
      return newViews;
    });
  };

  const filteredViews = useMemo(() => {
    return viewStates.filter((view) => {
      const matchesFilter =
        activeFilter === "all" ||
        (activeFilter === "system" && view.isSystem) ||
        (activeFilter === "custom" && !view.isSystem);

      const matchesSearch =
        !searchTerm ||
        view.name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesFilter && matchesSearch;
    });
  }, [viewStates, activeFilter, searchTerm]);

  return (
    <>
      <Drawer
        open={isOpen}
        direction="right"
        fixed
        modal={false}
        disablePreventScroll={true}
        noBodyStyles={true}
        dismissible={false}
      >
        <AppDrawerContent
          initialWidth={400}
          className={cn("max-w-[26rem]", className)}
        >
          <DrawerHeader className="h-auto">
            <DrawerTitle>
              <p className="text-xl font-semibold capitalize text-primary">
                {t("datatoolbar.views.viewsTitle")}
              </p>
            </DrawerTitle>
            <div className="flex gap-2 items-center">
              <SearchButtonInput
                placeholder={t("search")}
                inputValue={searchTerm}
                setInputValue={setSearchTerm}
                iconButtonSize="lg"
              />
              <IconButton size="lg" variant="iconNoBg" onClick={onClose}>
                <Close />
              </IconButton>
            </div>
          </DrawerHeader>
          <DrawerDescription>
            {t("datatoolbar.views.viewSelectionDescription")}
          </DrawerDescription>

          <div className="w-full flex flex-col gap-4 h-full overflow-y-auto px-4 py-4">
            <Tabs
              options={getViewFilterOptions(t, viewStates)}
              defaultValue="all"
              variant="pill"
              size="lg"
              valueSelected={(value) =>
                setActiveFilter(value as ViewFilterType)
              }
            />
            <DragDropContext
              onDragEnd={handleDragEnd}
              onDragUpdate={handleDragUpdate}
            >
              <Droppable
                droppableId="views-list"
                isDropDisabled={
                  filteredViews.length <= 1 || viewStates.length <= 1
                }
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="w-full flex flex-col"
                  >
                    <CheckboxGroup fullWidth spacing={0} color="var(--cta)">
                      {filteredViews.map((view, index) => (
                        <React.Fragment key={view.id}>
                          <NewDraggableViewItem
                            key={view.id}
                            view={view}
                            index={index}
                            onViewChange={handleViewChange}
                            onDeleteView={handleDeleteView}
                            onRename={handleRename}
                            i18n={i18n}
                            isDragDisabled={filteredViews.length <= 1}
                            isCheckboxDisabled={getIsCheckboxDisabled(view)}
                            checkboxClassName="p-[6px]"
                          />
                          {dragState.isDragging &&
                            dragState.destinationIndex === index && (
                              <div className="h-[1px] bg-cta w-full transition-opacity duration-200" />
                            )}
                        </React.Fragment>
                      ))}
                    </CheckboxGroup>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <DrawerFooter
            padded
            className="border-t-[0.5px] border-border-border1"
          >
            <div className="flex gap-2 w-full justify-end">
              <Button variant="secondary" onClick={onClose}>
                {t("cancel")}
              </Button>
              <Button onClick={handleApply}>{t("apply")}</Button>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>
      <DeleteViewModal
        isOpen={!!viewToDelete}
        setIsOpen={(isOpen) => !isOpen && setViewToDelete(null)}
        view={viewToDelete}
        onDelete={handleConfirmDelete}
      />
    </>
  );
};

export default NewViewsSelection;
